<template>
  <div v-if="initialised && ready">
    <b-btn v-if="!invisible" v-b-modal="uniqueClass" size="sm" variant="info">
      <slot>
        <font-awesome-icon :icon="['fal', 'comments']" />
        Chat
      </slot>
    </b-btn>

    <b-modal
      :id="uniqueClass"
      ref="contactCustomerModal"
      size="xl"
      title-class="w-100 row"
      @show="showingModal"
    >
      <template #modal-title>
        <div class="col">Contact Customer: {{ contact.name }}</div>
        <div class="col text-right">JOB #{{ jobId }}</div>
      </template>
      <div :class="`channel-${channels}`" class="row">
        <div class="col">
          <form>
            <div class="form-group">
              <div class="row mb-2">
                <div class="col d-flex align-items-center">
                  <label class="form-control-label mb-0 text-18">Message</label>
                </div>
                <div class="col">
                  <select
                    v-if="computedContactCustomerTemplates.length > 0"
                    class="form-control"
                    @change="contactCustomerTemplateChosen"
                  >
                    <option value="">Choose from predefined template</option>
                    <option
                      v-for="template in computedContactCustomerTemplates"
                      :key="template.id"
                      :value="template.template"
                    >
                      {{ template.name }}
                    </option>
                  </select>
                </div>
              </div>
              <textarea ref="message" v-model="message" class="form-control" rows="10" />
            </div>
            <div class="row">
              <div class="col-8">
                <div class="text-muted">
                  <div>
                    Total
                    {{ attachmentTotalCount }} attachment{{ attachmentTotalCount > 1 ? "s" : "" }}
                    to be
                    <span v-if="channels === 'mail'">emailed</span>
                    <span v-if="channels === 'sms'">messaged</span>
                  </div>

                  <div :class="attachmentsTotal > 10000000 ? 'text-red' : ''">
                    Total attachment size: {{ attachmentTotalInMb }}mb
                  </div>
                </div>
              </div>
              <div class="col-4">
                <text-count :text="message" class="text-muted text-right" max="160" />
              </div>
            </div>

            <div>
              <div class="text-15 mt-2 mb-1">Shortcodes</div>

              <div class="py-1 mr-1 d-inline-block" @click="addShortcode('[sender_first_name]')">
                <generic-object class="cursor">[sender_first_name]</generic-object>
              </div>
              <div class="py-1 mr-1 d-inline-block" @click="addShortcode('[sender_full_name]')">
                <generic-object class="cursor">[sender_full_name]</generic-object>
              </div>
              <div class="py-1 mr-1 d-inline-block" @click="addShortcode('[recipient_first_name]')">
                <generic-object class="cursor">[recipient_first_name]</generic-object>
              </div>
              <div class="py-1 mr-1 d-inline-block" @click="addShortcode('[recipient_full_name]')">
                <generic-object class="cursor">[recipient_full_name]</generic-object>
              </div>
              <div class="py-1 mr-1 d-inline-block" @click="addShortcode('[job_number]')">
                <generic-object class="cursor">[job_number]</generic-object>
              </div>
              <div class="py-1 mr-1 d-inline-block" @click="addShortcode('[job_address]')">
                <generic-object class="cursor">[job_address]</generic-object>
              </div>
              <div class="py-1 mr-1 d-inline-block" @click="addShortcode('[job_brand_name]')">
                <generic-object class="cursor">[job_brand_name]</generic-object>
              </div>
              <div
                class="py-1 mr-1 d-inline-block"
                @click="addShortcode('[job_contact_first_name]')"
              >
                <generic-object class="cursor">[job_contact_first_name]</generic-object>
              </div>
              <div
                class="py-1 mr-1 d-inline-block"
                @click="addShortcode('[job_contact_full_name]')"
              >
                <generic-object class="cursor">[job_contact_full_name]</generic-object>
              </div>
              <div
                class="py-1 d-inline-block"
                @click="addShortcode('[billing_contact_first_name]')"
              >
                <generic-object class="cursor">[billing_contact_first_name]</generic-object>
              </div>
              <div class="py-1 d-inline-block" @click="addShortcode('[billing_contact_full_name]')">
                <generic-object class="cursor">[billing_contact_full_name]</generic-object>
              </div>
            </div>
          </form>
        </div>
        <div class="col">
          <div class="row">
            <div class="col py-1 text-18">Attachments</div>
          </div>
          <div class="row pt-2">
            <div class="col attachments-list">
              <div class="pb-2">Files</div>
              <ul v-if="files.length > 0" class="list-group overflow-y-auto" style="height: 500px">
                <li
                  v-for="file in files"
                  :key="file.id"
                  :class="selectedFileIds.includes(file.id) ? 'active' : ''"
                  class="list-group-item highlight cursor"
                  @click="toggleFile(file)"
                >
                  <div v-if="file.media[0] !== undefined" class="w-100">
                    <template
                      v-if="
                        file.media[0].mime_type === 'image/jpeg' ||
                        file.media[0].mime_type === 'image/jpg' ||
                        file.media[0].mime_type === 'image/gif' ||
                        file.media[0].mime_type === 'image/png'
                      "
                    >
                      <img :src="getFileThumbnail(file)" alt="" />
                    </template>
                    <template v-else-if="file.media[0].mime_type === 'application/pdf'">
                      PDF
                      <pdf-object :href="file.media[0].url" />
                    </template>
                    <template v-else>
                      File attachment <a :href="file.media[0].url" target="_blank">file</a>
                    </template>
                  </div>

                  <div class="text-muted text-12 mt-2">
                    {{ formatDate(file.created_at, "DD MMM YYYY@hh:mma") }}<br />
                    by {{ file.user ? file.user.name : "Anonymous" }}
                  </div>
                </li>
              </ul>
              <div v-else>
                <div class="text-muted">No file</div>
              </div>
            </div>
            <div class="col invoice-list">
              <div class="pb-2">Invoices & Quotes</div>
              <ul
                v-if="invoices.length > 0"
                class="list-group overflow-y-auto"
                style="height: 500px"
              >
                <li
                  v-for="invoice in invoices"
                  :key="invoice.id"
                  :class="selectedInvoiceIds.includes(invoice.id) ? 'active' : ''"
                  class="list-group-item highlight cursor px-2 pt-2 pb-1"
                  @click="toggleInvoice(invoice)"
                >
                  <div class="d-flex align-items-center">
                    <invoice-object
                      :entity-id="invoice.id"
                      :invoice="invoice"
                      :invoice-number="invoice.invoice_number"
                      :open-new="true"
                    />

                    <span class="text-12 text-muted ml-1">{{ formatCurrency(invoice.total) }}</span>

                    <b-button
                      class="ml-auto"
                      size="xsm"
                      variant="light"
                      @click.stop="insertLink(invoice)"
                    >
                      <font-awesome-icon :icon="['fal', 'link']" />
                    </b-button>
                  </div>
                  <div class="text-muted mt-1 text-12">
                    {{ formatLocalDate(invoice.created_at) }}
                  </div>
                </li>
              </ul>
              <div v-else>
                <div class="text-muted">No invoice</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <template #modal-footer>
        <div class="row">
          <div class="col d-flex align-items-center">
            <span class="mr-2"> Contact Channels: </span>
            <div class="form-check form-check-inline">
              <label class="form-check-label">
                <input v-model="channels" class="form-check-input" type="radio" value="mail" />
                Email <span class="text-muted">{{ contact.email }}</span>
                <span v-if="!isValidEmail(contact.email)" class="pl-1 text-danger"
                  >Invalid Email</span
                >
              </label>
            </div>
            <div class="form-check form-check-inline">
              <label class="form-check-label">
                <input v-model="channels" class="form-check-input" type="radio" value="sms" />
                SMS <span class="text-muted">{{ contact.mobile }}</span>
                <span v-if="!isValidMobile(contact.mobile)" class="pl-1 text-danger"
                  >Invalid Mobile</span
                >
              </label>
            </div>

            <button
              v-tooltip="{
                content: invalid,
                classes: ['badge-theme'],
              }"
              :class="invalid ? 'disabled' : ''"
              class="btn btn-primary ml-auto"
              type="button"
              @click.prevent="send"
            >
              <font-awesome-icon v-if="sending" :icon="['fal', 'spinner']" spin />
              <font-awesome-icon v-else :icon="['fal', 'envelope-open-text']" />
              Send message
            </button>
          </div>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script type="text/babel">
import orderBy from "lodash/orderBy";
import format from "../../mixins/format";
import EventBus from "../../imports/event-bus";
import TextCount from "./TextCount";

export default {
  components: { TextCount },

  mixins: [format],

  props: {
    jobId: {
      type: Number,
      required: false,
    },

    invisible: {
      type: Boolean,
      required: false,
      default: true,
    },
  },

  data() {
    return {
      initialised: false,
      uniqueClass: null,
      message: null,
      channels: "mail",
      customer: null,
      contact: null,
      sending: false,

      contactCustomerTemplates: [],

      attachmentsTotal: 0,

      files: [],
      selectedFileIds: [],

      invoices: [],
      selectedInvoiceIds: [],
    };
  },

  computed: {
    ready() {
      return this.$lodash.has(this.customer, "id") && this.$lodash.has(this.contact, "id");
    },

    attachmentTotalCount() {
      return this.selectedFileIds.length + this.selectedInvoiceIds.length;
    },

    attachmentTotalInMb() {
      if (this.attachmentsTotal < 1000000 && this.attachmentsTotal > 0) {
        return "Less than 1";
      }

      const result = Number(this.attachmentsTotal / 1000 / 1000);
      return (Math.round(result * 100) / 100).toFixed(2);
    },

    invalid() {
      if (this.channels === "mail" && !this.isValidEmail(this.contact.email)) {
        return "The contact has an invalid email";
      }

      if (this.channels === "sms" && !this.isValidMobile(this.contact.mobile)) {
        return "The contact has an invalid mobile";
      }

      if (this.sending) {
        return "Sending a message right now";
      }

      if (this.message.length === 0) {
        return "Please enter your message first";
      }

      if (this.attachmentsTotal >= 10000000) {
        return "The total attachment size is too large";
      }

      return false;
    },

    computedContactCustomerTemplates() {
      const templates = this.contactCustomerTemplates.filter((template) => {
        const channelKey = this.channels === "mail" ? "email" : "message";
        return template.channel === channelKey;
      });
      return orderBy(templates, ["name"], ["asc"]);
    },
  },

  watch: {
    channels() {
      this.selectedFileIds = [];
      this.attachmentsTotal = 0;
    },
  },

  mounted() {
    this.init();
  },

  methods: {
    init() {
      this.initialised = false;

      this.message = "";
      this.brand_id = null;
      this.channels = "mail";
      this.customer = null;
      this.contact = null;
      this.sending = false;

      this.attachmentsTotal = 0;
      this.files = [];
      this.selectedFileIds = [];
      this.invoices = [];
      this.selectedInvoiceIds = [];

      this.uniqueClass = `contact-customer-${window.generateUUID()}`;

      this.initialised = true;
    },

    showingModal() {
      this.getAttachments();
      this.getBrandContactCustomerTemplates();
    },

    getAttachments() {
      this.$http.get(`/jobs/${this.jobId}/job-notes?media_only=true&limit=999`).then((response) => {
        this.files = response.body.data;
      });

      this.$http.get(`/jobs/${this.jobId}/invoices?limit=999`).then((response) => {
        this.invoices = response.body.data;
      });
    },

    getBrandContactCustomerTemplates() {
      return this.$http
        .get(`/jobs/${this.jobId}/brand-contact-templates?target=customer&limit=999`)
        .then((response) => {
          this.contactCustomerTemplates = response.body.data;
        });
    },

    contactCustomerTemplateChosen(event) {
      if (event.target.value === "") {
        return;
      }

      this.message = event.target.value;
      this.$refs.message.focus();

      const reserved = "#YOUR_INPUT#";
      const atIndex = event.target.value.indexOf(reserved);
      if (atIndex > 0) {
        setTimeout(() => {
          this.$refs.message.setSelectionRange(atIndex, atIndex + reserved.length);
        });
      }
    },

    getFileThumbnail(file) {
      if (this.$lodash.has(file, "media[0].thumbnails[300]")) {
        return file.media[0].thumbnails[300];
      }

      if (this.$lodash.has(file, "media[0].thumbnails[600]")) {
        return file.media[0].thumbnails[600];
      }

      if (this.$lodash.has(file, "media[0].url")) {
        return file.media[0].url;
      }

      return "";
    },

    toggleFile(file) {
      if (this.selectedFileIds.includes(file.id)) {
        const index = this.selectedFileIds.indexOf(file.id);
        if (index > -1) {
          this.selectedFileIds.splice(index, 1);
        }
        this.attachmentsTotal -= this.$lodash.get(file, "media[0].size", 0);
        return;
      }

      this.selectedFileIds.push(file.id);
      this.attachmentsTotal += this.$lodash.get(file, "media[0].size", 0);
    },

    toggleInvoice(invoice) {
      if (this.channels === "sms") {
        this.addShortcode(`[job_invoice_landing_url id=${invoice.id}]`);
        return;
      }

      if (this.selectedInvoiceIds.includes(invoice.id)) {
        const index = this.selectedInvoiceIds.indexOf(invoice.id);
        if (index > -1) {
          this.selectedInvoiceIds.splice(index, 1);
        }

        return;
      }

      this.selectedInvoiceIds.push(invoice.id);
    },

    addShortcode(shortcode) {
      if (this.$refs.message) {
        const textareaRef = this.$refs.message;

        if (textareaRef.selectionStart) {
          const before = this.message.substr(0, textareaRef.selectionStart);
          let after = this.message.substr(textareaRef.selectionStart, this.message.length);
          const at = `${before}${shortcode}`;

          if (textareaRef.selectionEnd > textareaRef.selectionStart) {
            // Currently selected text exist, remove selection from after
            after = after.substr(textareaRef.selectionEnd - textareaRef.selectionStart);
          }

          this.message = `${before}${shortcode}${after}`;

          setTimeout(() => {
            textareaRef.focus();
            textareaRef.setSelectionRange(at.length, at.length);
          });

          return;
        }
      }

      this.message = `${this.message}${shortcode}`;
    },

    insertLink(invoice) {
      this.addShortcode(`[job_invoice_landing_url id=${invoice.id}]\n`);
    },

    setChannel(channel) {
      this.channels = channel;
    },

    setBrandId(brandId) {
      this.brand_id = brandId;
    },

    setCustomer(customer) {
      this.customer = customer;
    },

    setContact(contact) {
      this.contact = contact;
    },

    showModal() {
      this.$nextTick(() => {
        this.$refs.contactCustomerModal.show();
      });
    },

    send() {
      if (this.invalid) {
        return;
      }

      this.sending = true;

      this.$http
        .post(`/contacts/${this.contact.id}/contact`, {
          message: this.message,
          brand_id: this.brand_id,
          channels: this.channels,
          context: {
            // Assuming we can access job id via current router
            // This works because currently this contact-customer component
            // is only used in dispatch board
            job_id: this.$lodash.get(this, "$router.currentRoute.params.id"),
          },
          files: this.selectedFileIds,
          invoices: this.selectedInvoiceIds,
        })
        .then((response) => {
          if (response.body === "done") {
            window.toastr.success(
              `Your message has been successfully sent to ${this.contact.name} via ${this.channels}.`,
              "Done"
            );
            this.$refs.contactCustomerModal.hide();

            this.init();

            EventBus.$emit("chated-to-contact");
          } else {
            window.toastr.error(
              `An error occurred when trying to sent message to ${this.contact.name} via ${this.channels}.`,
              "Error"
            );
          }
        });
    },

    isValidEmail(email) {
      return window.isValidEmail(email);
    },

    isValidMobile(mobile) {
      return window.isValidMobile(mobile);
    },
  },
};
</script>
